import { forwardRef, useCallback, useImperativeHandle, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import type { BoxTruckAssetProfile, TruckDraft } from '@numeo/types-core'
import { BOX_TRUCK_DOCK_HEIGHT_OPTIONS, BOX_TRUCK_EQUIPMENT_CAPABILITIES, boxTruckStoredDate, formatBoxTruckDockHeight } from '@numeo/types-core'
import { Autocomplete, DateInput, Select, TextField } from 'components/ds2'
import { STATES_LIST } from 'pages/dashboard/pages/settings/const'
import { BoxTruckAssetDocuments } from '@/features/box-truck-loads/components/BoxTruckAssetDocuments'
import type { BoxTruckPendingAssetDocumentUploads } from '@/features/box-truck-loads/services/boxTruckProfilesApi'
import { BOX_TRUCK_EQUIPMENT_FILTER_OPTIONS } from '@/features/box-truck-loads/utils/format'
import { useBoxTruckAccess } from '@/features/box-truck-loads/hooks/useBoxTruckAccess'
import { BoxTruckVehicleAssignments } from './BoxTruckVehicleAssignments'
import { BoxTruckSectionHeading, boxTruckSectionClass } from './BoxTruckFormSection'
import { AutofillField } from '@/features/box-truck-loads/components/documentAutofill/AutofillField'
import type { PendingDriverSeat, PendingTruckAssignment } from 'store/trucksSlice'

interface BoxTruckVehicleDetailsProps {
    /** A saved vehicle OR one still being filled in — the claim form edits a draft with no id yet and only
     *  as much of the Box Truck block as has been answered. A `Truck` is assignable to it. */
    truckData: TruckDraft
    ownerDriverIds?: string[]
    /** Drivers, dispatchers and the owner. Off where the caller is editing a DRIVER's equipment rather than
     *  staffing a vehicle — assigning people to a truck from inside a driver's profile is not that flow. */
    showAssignments?: boolean
    /** Unit, VIN and Vehicle Type. Off where the surrounding form already asks for them, which is the whole
     *  vehicle wizard: the same three inputs appearing twice on one page is what this flag exists to stop. */
    showIdentity?: boolean
    /** The create flow stays active after POST while failed document uploads are retried with the new ID. */
    validateAllDates?: boolean
    onUpdate: (field: string, value: string | number | null | string[]) => void
    /** The driver and the owner being created in the form behind this one. Neither has an id yet: each is an
     *  option in its picker (see `BoxTruckVehicleAssignments`). */
    pendingAssignment?: PendingTruckAssignment
    onPendingDriverSeatChange?: (seat: PendingDriverSeat) => void
    onPendingOwnerChosenChange?: (chosen: boolean) => void
    onVehicleTypeChange?: (vehicleType: string) => void
    /** Documents chosen before the vehicle exists. Supplied by the create form, which uploads them once the
     *  truck has an id; absent when editing, where each file is sent as it is chosen. */
    pendingDocuments?: BoxTruckPendingAssetDocumentUploads
    onPendingDocumentsChange?: Dispatch<SetStateAction<BoxTruckPendingAssetDocumentUploads>>
    /** Sections drawn flat under dividers instead of as cards: Quick Add Truck lays its form out that way, as Quick Add Driver does. */
    bare?: boolean
}

type VehicleDateField = 'expirationDate' | 'insuranceExpirationDate'

const VEHICLE_DATE_FIELDS = ['expirationDate', 'insuranceExpirationDate'] as const satisfies readonly VehicleDateField[]

const VEHICLE_DATE_LABELS: Record<VehicleDateField, string> = {
    expirationDate: 'Registration Expiry',
    insuranceExpirationDate: 'Insurance Expiry',
}

const vehicleDateError = (field: VehicleDateField, value: string): string | undefined =>
    value && !boxTruckStoredDate(value) ? `${VEHICLE_DATE_LABELS[field]} must use YYYY-MM-DD` : undefined

export interface BoxTruckVehicleDetailsHandle {
    validate: () => boolean
}

/** Keep imported values selectable without offering a second row for a casing variant. */
export const optionsIncludingStored = (offered: readonly string[], stored: string | string[] | undefined): string[] => {
    const storedValues = Array.isArray(stored) ? stored : stored ? [stored] : []
    const seen = new Set(offered.map((value) => value.toLowerCase()))
    return [...offered, ...storedValues.filter((value) => !seen.has(value.toLowerCase()) && seen.add(value.toLowerCase()))]
}

/** Display the canonical option spelling without rewriting the stored value until the operator edits it. */
export const asOfferedSpelling = (offered: readonly string[], stored: string | undefined): string =>
    offered.find((value) => value.toLowerCase() === stored?.toLowerCase()) ?? stored ?? ''

const numericValue = (value: string): number | null => (value === '' ? null : Number(value))

export const BoxTruckVehicleDetails = forwardRef<BoxTruckVehicleDetailsHandle, BoxTruckVehicleDetailsProps>(
    (
        {
            truckData,
            ownerDriverIds,
            showAssignments = true,
            showIdentity = true,
            validateAllDates = !truckData._id,
            onUpdate,
            onVehicleTypeChange,
            pendingAssignment,
            onPendingDriverSeatChange,
            onPendingOwnerChosenChange,
            pendingDocuments,
            onPendingDocumentsChange,
            bare = false,
        },
        validationRef
    ) => {
        const { management: canManageDocuments } = useBoxTruckAccess()
        const boxTruck = (truckData.boxTruck ?? {}) as Partial<BoxTruckAssetProfile>
        const registrationExpirationDate = boxTruck.expirationDate
        const insuranceExpirationDate = boxTruck.insuranceExpirationDate
        const vehicleId = String(truckData._id ?? '')
        const touchedDateFields = useRef({ vehicleId, fields: new Set<VehicleDateField>() })
        if (touchedDateFields.current.vehicleId !== vehicleId) {
            touchedDateFields.current = { vehicleId, fields: new Set<VehicleDateField>() }
        }
        const dateInputRefs = useRef<Partial<Record<VehicleDateField, { focus: () => void } | null>>>({})
        const [dateErrorState, setDateErrorState] = useState<{ vehicleId: string; errors: Partial<Record<VehicleDateField, string>> }>({
            vehicleId,
            errors: {},
        })
        const dateErrors = dateErrorState.vehicleId === vehicleId ? dateErrorState.errors : {}
        const validateDates = useCallback((): boolean => {
            const nextErrors: Partial<Record<VehicleDateField, string>> = {}
            const fieldsToValidate = validateAllDates ? VEHICLE_DATE_FIELDS : [...touchedDateFields.current.fields]
            fieldsToValidate.forEach((field) => {
                const value = field === 'expirationDate' ? registrationExpirationDate : insuranceExpirationDate
                const error = vehicleDateError(field, String(value ?? ''))
                if (error) nextErrors[field] = error
            })
            setDateErrorState({ vehicleId, errors: nextErrors })
            const firstInvalidField = Object.keys(nextErrors)[0] as VehicleDateField | undefined
            if (firstInvalidField) dateInputRefs.current[firstInvalidField]?.focus()
            return Object.keys(nextErrors).length === 0
        }, [insuranceExpirationDate, registrationExpirationDate, validateAllDates, vehicleId])
        useImperativeHandle(validationRef, () => ({ validate: validateDates }), [validateDates])
        const text = (field: keyof BoxTruckAssetProfile, label: string, placeholder?: string) => (
            <TextField
                variant="secondary"
                label={label}
                placeholder={placeholder}
                value={String(boxTruck[field] ?? '')}
                onChange={(event) => onUpdate(`boxTruck.${field}`, event.target.value)}
            />
        )
        /** The date input takes YYYY-MM-DD; preserve any legacy free text visibly until it is edited. */
        const date = (field: VehicleDateField) => {
            const label = VEHICLE_DATE_LABELS[field]
            const stored = String(boxTruck[field] ?? '')
            const updateDate = (value: string) => {
                touchedDateFields.current.fields.add(field)
                setDateErrorState((current) => ({
                    vehicleId,
                    errors: { ...(current.vehicleId === vehicleId ? current.errors : {}), [field]: vehicleDateError(field, value) },
                }))
                onUpdate(`boxTruck.${field}`, value)
            }
            const error = dateErrors[field]
            if (stored !== '' && !boxTruckStoredDate(stored)) {
                return (
                    <TextField
                        ref={(input) => {
                            dateInputRefs.current[field] = input
                        }}
                        variant="secondary"
                        label={label}
                        placeholder="YYYY-MM-DD"
                        value={stored}
                        isInvalid={Boolean(error)}
                        errorMessage={error}
                        onChange={(event) => updateDate(event.target.value)}
                    />
                )
            }
            return (
                <DateInput
                    focusRef={(handle) => {
                        dateInputRefs.current[field] = handle
                    }}
                    variant="secondary"
                    label={label}
                    value={boxTruckStoredDate(stored) ?? ''}
                    error={error}
                    onChange={updateDate}
                />
            )
        }
        const number = (field: keyof BoxTruckAssetProfile, label: string) => (
            <TextField
                variant="secondary"
                inputType="number"
                label={label}
                value={String(boxTruck[field] ?? '')}
                onChange={(event) => onUpdate(`boxTruck.${field}`, numericValue(event.target.value))}
            />
        )

        const sectionClass = boxTruckSectionClass(bare)

        return (
            <div className={bare ? 'space-y-4 px-3 pb-3' : 'space-y-5 p-3'}>
                {showAssignments && (
                    <BoxTruckVehicleAssignments
                        bare={bare}
                        truckData={truckData}
                        ownerDriverIds={ownerDriverIds}
                        onUpdate={onUpdate}
                        pendingAssignment={pendingAssignment}
                        onPendingDriverSeatChange={onPendingDriverSeatChange}
                        onPendingOwnerChosenChange={onPendingOwnerChosenChange}
                    />
                )}

                <section className={sectionClass}>
                    <BoxTruckSectionHeading bare={bare}>Vehicle Details</BoxTruckSectionHeading>
                    {/* Two columns in the modal, like every other Quick Add section: `xl` reads the viewport, not the modal. */}
                    <div className={bare ? 'grid grid-cols-1 gap-4 md:grid-cols-2' : 'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3'}>
                        {showIdentity && (
                            <>
                                <TextField
                                    variant="secondary"
                                    label="Unit"
                                    value={truckData.unitNumber || ''}
                                    onChange={(event) => onUpdate('unitNumber', event.target.value)}
                                />
                                <TextField
                                    variant="secondary"
                                    label="VIN"
                                    value={truckData.vin || ''}
                                    onChange={(event) => onUpdate('vin', event.target.value)}
                                />
                                <Select
                                    variant="secondary"
                                    label="Vehicle Type"
                                    required
                                    value={boxTruck.vehicleType || ''}
                                    options={BOX_TRUCK_EQUIPMENT_FILTER_OPTIONS.map((value) => ({ value, label: value }))}
                                    onChange={(value) => {
                                        if (!value) return
                                        if (onVehicleTypeChange) onVehicleTypeChange(value)
                                        else onUpdate('boxTruck.vehicleType', value)
                                    }}
                                />
                            </>
                        )}
                        <AutofillField field="make">
                            <TextField
                                variant="secondary"
                                label="Make"
                                value={truckData.make || ''}
                                onChange={(event) => onUpdate('make', event.target.value)}
                            />
                        </AutofillField>
                        <TextField
                            variant="secondary"
                            label="Model"
                            value={truckData.model || ''}
                            onChange={(event) => onUpdate('model', event.target.value)}
                        />
                        <AutofillField field="year">
                            <TextField
                                variant="secondary"
                                inputType="number"
                                label="Vehicle Year"
                                value={String(truckData.year ?? '')}
                                onChange={(event) => onUpdate('year', numericValue(event.target.value))}
                            />
                        </AutofillField>
                        <AutofillField field="boxTruck.payloadLbs">{number('payloadLbs', 'Payload (lbs)')}</AutofillField>
                        <AutofillField field="boxTruck.gvwLbs">{number('gvwLbs', 'GVW (lbs)')}</AutofillField>
                        <AutofillField field="boxTruck.registrationPlate">{text('registrationPlate', 'Registration Plate')}</AutofillField>
                        <AutofillField field="boxTruck.expirationDate">{date('expirationDate')}</AutofillField>
                        <AutofillField field="boxTruck.registrationState">
                            <Select
                                variant="secondary"
                                label="Registration State"
                                value={boxTruck.registrationState || ''}
                                options={[
                                    { value: '', label: 'Select state' },
                                    ...STATES_LIST.map((state) => ({ value: state.value, label: state.label })),
                                    // A code the list does not name (DC, from a document) is still shown, as the stored value it is.
                                    ...(boxTruck.registrationState && !STATES_LIST.some((state) => state.value === boxTruck.registrationState)
                                        ? [{ value: boxTruck.registrationState, label: boxTruck.registrationState }]
                                        : []),
                                ]}
                                onChange={(value) => onUpdate('boxTruck.registrationState', value)}
                            />
                        </AutofillField>
                        <AutofillField field="boxTruck.insuranceCompany">{text('insuranceCompany', 'Insurance Company')}</AutofillField>
                        <AutofillField field="boxTruck.insuranceExpirationDate">{date('insuranceExpirationDate')}</AutofillField>
                        <AutofillField field="boxTruck.insurancePolicyNumber">{text('insurancePolicyNumber', 'Policy #')}</AutofillField>
                        {truckData.boxTruck?.team && text('team', 'Imported team (legacy)')}
                    </div>
                </section>

                <section className={sectionClass}>
                    <BoxTruckSectionHeading bare={bare}>Useful Cargo Dimensions (inches)</BoxTruckSectionHeading>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        {number('cargoLengthIn', 'Length')}
                        {number('cargoWidthIn', 'Width')}
                        {number('cargoHeightIn', 'Height')}
                    </div>
                    <BoxTruckSectionHeading bare={bare} className="pt-2">
                        Door Opening (inches)
                    </BoxTruckSectionHeading>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {number('doorWidthIn', 'Width')}
                        {number('doorHeightIn', 'Height')}
                    </div>
                </section>

                <section className={sectionClass}>
                    <BoxTruckSectionHeading bare={bare}>Equipment &amp; Dock Height</BoxTruckSectionHeading>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Autocomplete
                            variant="secondary"
                            label="Equipment capabilities"
                            placeholder="Select equipment"
                            multiple
                            options={optionsIncludingStored(BOX_TRUCK_EQUIPMENT_CAPABILITIES, boxTruck.equipmentCapabilities).map((value) => ({
                                value,
                                label: value,
                            }))}
                            value={(boxTruck.equipmentCapabilities ?? []).map((value) => asOfferedSpelling(BOX_TRUCK_EQUIPMENT_CAPABILITIES, value))}
                            onChange={(values) => onUpdate('boxTruck.equipmentCapabilities', values)}
                        />
                        <Select
                            variant="secondary"
                            label="Dock Height / Ramps"
                            value={asOfferedSpelling(BOX_TRUCK_DOCK_HEIGHT_OPTIONS, boxTruck.dockHeightRamps)}
                            options={[
                                { value: '', label: 'Select dock height / ramps' },
                                ...optionsIncludingStored(BOX_TRUCK_DOCK_HEIGHT_OPTIONS, boxTruck.dockHeightRamps).map((value) => ({
                                    value,
                                    label: formatBoxTruckDockHeight(value) ?? value,
                                })),
                            ]}
                            onChange={(value) => onUpdate('boxTruck.dockHeightRamps', value)}
                        />
                    </div>
                </section>

                {/* Shown on the CREATE form too, where there is no id yet: a registration and an insurance
                certificate are what the operator has in hand while they are typing the plate, and the files
                wait in `pendingDocuments` until the truck exists. Without a handler for them there is nowhere
                to hold the files, so the section stays hidden rather than silently dropping them. */}
                {canManageDocuments && (truckData._id || onPendingDocumentsChange) && (
                    <section className={sectionClass}>
                        {bare ? (
                            <BoxTruckSectionHeading bare>
                                Documents <span className="font-normal text-foreground-muted">(optional)</span>
                            </BoxTruckSectionHeading>
                        ) : (
                            <div>
                                <BoxTruckSectionHeading bare={false}>Documents</BoxTruckSectionHeading>
                                <p className="mt-1 text-sm text-foreground-muted">
                                    Drop insurance, registration, or photos onto the matching card, or click a card to browse.
                                    {!truckData._id && ' Files selected here will upload after you add the truck.'}
                                </p>
                            </div>
                        )}
                        <BoxTruckAssetDocuments
                            assetId={truckData._id ? String(truckData._id) : undefined}
                            pendingDocuments={pendingDocuments}
                            onPendingDocumentsChange={onPendingDocumentsChange}
                        />
                        {bare && !truckData._id && <p className="text-xs text-foreground-muted">Selected files upload when you add the truck.</p>}
                    </section>
                )}
            </div>
        )
    }
)
